/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Bharat Connect (BBPS) & NCMC Transit Ledger + MDM 1,500 Batch Sync
 * PATH: src/routes/bill_payments/BillPaymentList.js
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Row,
  Col,
  Card,
  Tag,
  Tooltip,
  Typography,
  Input,
  Select,
  Modal,
  Radio,
  Progress,
  Statistic,
  Divider,
  Alert,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  CopyOutlined,
  CloudUploadOutlined,
  SyncOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import * as XLSX from "xlsx";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { formatCurrency } from "utils/common";
import api from "utils/api";
import "./BillPaymentList.css";

const { Text, Title } = Typography;
const { Option } = Select;

const MAX_BATCH_SIZE = 500;

function BillPaymentList() {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // MDM Sync Modal State
  const [mdmModalVisible, setMdmModalVisible] = useState(false);
  const [mdmStats, setMdmStats] = useState({
    total_billers: 0,
    synced_xml_count: 0,
    pending_xml_count: 0,
    live_billers: 0,
  });
  const [mdmMode, setMdmMode] = useState("upsert"); // 'upsert' | 'modify' | 'deactivate'
  const [parsedRows, setParsedRows] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [batchUploading, setBatchUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState("");
  const [xmlSyncing, setXmlSyncing] = useState(false);

  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch Live BBPS & Utility Bill Transactions
  const getData = useCallback(
    async (query) => {
      try {
        setIsTableLoading(true);
        const activeQuery = query || parseQueryParams(location);

        const response = await api.post("bill-payments-list", activeQuery);

        if (response && response.data) {
          const resData = response.data;
          setRecords(resData.data || resData.records || []);
          setPage(Number(resData.current_page) || 1);
          setPerPage(Number(resData.per_page) || 10);
          setTotalCount(Number(resData.total) || 0);
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message ||
            "Failed to synchronize BBPS utility ledger."
        );
        setRecords([]);
        setTotalCount(0);
      } finally {
        setIsTableLoading(false);
      }
    },
    [location]
  );

  // Fetch MDM Status Counts
  const fetchMdmStats = useCallback(async () => {
    try {
      const res = await api.get("mdm-status");
      if (res?.data?.status === 1) {
        setMdmStats({
          total_billers: res.data.total_billers || 0,
          synced_xml_count: res.data.synced_xml_count || 0,
          pending_xml_count: res.data.pending_xml_count || 0,
          live_billers: res.data.live_billers || 0,
        });
      }
    } catch (e) {
      // silent fallback
    }
  }, []);

  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.status) setStatusFilter(query.status);
    if (query.keyword) setSearchTerm(query.keyword);
    getData(query);
  }, [location, getData]);

  const openMdmModal = () => {
    setMdmModalVisible(true);
    fetchMdmStats();
  };

  // Parse .xlsx or .csv file in browser and prepare 1,500-row chunks
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setUploadProgress(0);
    setUploadStatusText("Reading spreadsheet file...");

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        setParsedRows(jsonData);
        const totalBatches = Math.ceil(jsonData.length / MAX_BATCH_SIZE);
        setUploadStatusText(
          `Loaded ${jsonData.length.toLocaleString()} biller rows (${totalBatches} batches of max ${MAX_BATCH_SIZE}).`
        );
        message.success(
          `Parsed ${jsonData.length.toLocaleString()} rows from ${file.name}`
        );
      } catch (err) {
        message.error("Failed to parse spreadsheet. Please upload a valid .xlsx or .csv file.");
        setParsedRows([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Execute Sequential 1,500-Row Batch Upload
  const startBatchIngestion = async () => {
    if (!parsedRows.length) {
      message.warning("Please select an MDM .xlsx or .csv file first.");
      return;
    }

    setBatchUploading(true);
    setUploadProgress(0);

    const totalRows = parsedRows.length;
    const totalBatches = Math.ceil(totalRows / MAX_BATCH_SIZE);
    let totalProcessed = 0;

    try {
      for (let i = 0; i < totalBatches; i++) {
        const start = i * MAX_BATCH_SIZE;
        const chunk = parsedRows.slice(start, start + MAX_BATCH_SIZE);

        setUploadStatusText(
          `Uploading Batch ${i + 1} of ${totalBatches} (${chunk.length} billers)...`
        );

        const res = await api.post("mdm-import-batch", {
          mode: mdmMode,
          rows: chunk,
        });

        if (res?.data?.status === 1) {
          totalProcessed +=
            (res.data.processed_count || 0) + (res.data.deactivated_count || 0);
        }

        const percent = Math.round(((i + 1) / totalBatches) * 100);
        setUploadProgress(percent);
      }

      setUploadStatusText(
        `Completed! Successfully processed ${totalProcessed.toLocaleString()} billers across ${totalBatches} batches.`
      );
      message.success("Bharat Connect MDM file ingested successfully!");
      fetchMdmStats();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Error during MDM batch ingestion."
      );
    } finally {
      setBatchUploading(false);
    }
  };

  // Trigger BillAvenue XML Sync (1,500 billers per call)
  const handleSyncXmlBatch = async () => {
    setXmlSyncing(true);
    try {
      const res = await api.post("mdm-sync-xml", { limit: MAX_BATCH_SIZE });
      if (res?.data?.status === 1) {
        message.success(res.data.message || "Synced 1,500 biller XML configs.");
        fetchMdmStats();
      } else {
        message.warning(res?.data?.message || "BillAvenue XML sync returned a notice.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to sync XML with BillAvenue."
      );
    } finally {
      setXmlSyncing(false);
    }
  };

  const executeSearch = useCallback(
    (value) => {
      const trimmed = (value || "").trim();
      let query = parseQueryParams(location);
      query = { ...query, page: 1 };

      if (trimmed) query.keyword = trimmed;
      else delete query.keyword;

      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams(query),
      });
    },
    [location, navigate]
  );

  const debouncedSearch = useRef(
    _.debounce((val) => executeSearch(val), 450)
  ).current;

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
  };

  const renderRupee = (val) => {
    const num = Number(val) || 0;
    try {
      if (typeof formatCurrency === "function") return formatCurrency(num);
    } catch (e) {}
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  };

  const copyToClipboard = (text, label) => {
    if (!text || text === "N/A") return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    let query = parseQueryParams(location);
    if (val) query = { ...query, page: 1, status: val };
    else delete query.status;
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: pagination.current,
      per_page: pagination.pageSize,
    };
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setStatusFilter("");
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams({ page: 1 }),
    });
  };

  const columns = [
    {
      title: "SR",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span className="bp-sr">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Txn Ref ID / Order ID",
      width: 230,
      render: (_, record) => {
        const txnId =
          record.txnRefId ||
          record.order_id ||
          record.requestId ||
          record.reference_id ||
          "N/A";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: "#0f172a",
                background: "#f1f5f9",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {txnId}
            </span>
            {txnId !== "N/A" && (
              <Tooltip title="Copy Transaction ID">
                <CopyOutlined
                  style={{ cursor: "pointer", color: "#0284c7", fontSize: 13 }}
                  onClick={() => copyToClipboard(txnId, "Transaction ID")}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Consumer & Utility Details",
      width: 220,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13.5 }}>
            {record.username || record.customer_name || "Consumer"}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "#475569",
              fontFamily: "monospace",
            }}
          >
            +91 {record.user_mobile || record.mobile || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Biller & Gateway",
      width: 210,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>
            {record.operator_name || record.biller_name || "Utility Biller"}
          </span>
          <Tag
            color="blue"
            style={{ width: "fit-content", fontSize: 10, borderRadius: 4 }}
          >
            NPCI BBPS Switch
          </Tag>
        </div>
      ),
    },
    {
      title: "Bill Amount",
      width: 140,
      render: (_, record) => (
        <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13.5 }}>
          {renderRupee(record.amount || record.final_amount || record.set_amount)}
        </span>
      ),
    },
    {
      title: "Timestamp (IST)",
      width: 170,
      render: (_, record) => {
        const timeVal =
          record.date_time || record.created_at || record.date || "N/A";
        return <span style={{ fontSize: 12, color: "#64748b" }}>{timeVal}</span>;
      },
    },
    {
      title: "Status",
      width: 120,
      align: "center",
      render: (_, record) => {
        const status = (record.status || "").toUpperCase();
        const isSuccess = status === "SUCCESS";
        const isPending = status === "PENDING" || status === "INPROCESS";
        return (
          <Tag
            icon={
              isSuccess ? (
                <CheckCircleFilled />
              ) : isPending ? (
                <ClockCircleFilled />
              ) : (
                <CloseCircleFilled />
              )
            }
            color={isSuccess ? "success" : isPending ? "warning" : "error"}
            style={{ borderRadius: 6, fontWeight: 600, padding: "2px 8px" }}
          >
            {status || "SUCCESS"}
          </Tag>
        );
      },
    },
  ];

  return (
    <div className="bill-payment-container">
      <Card className="bill-payment-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <div className="bill-payment-title-wrap">
              <AppstoreOutlined className="header-bp-icon" />
              <div>
                <Title level={4} className="bill-payment-main-heading">
                  Bharat Connect (BBPS) &amp; NCMC Transit
                </Title>
                <Text type="secondary" className="bill-payment-subtext">
                  Electricity, Water, Gas, FASTag, Municipal &amp; Metro Card
                  settlements
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={16} className="bill-payment-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              <Select
                placeholder="All Statuses"
                value={statusFilter || undefined}
                onChange={handleStatusChange}
                allowClear
                className="bill-payment-status-select"
              >
                <Option value="success">Success</Option>
                <Option value="pending">Pending</Option>
                <Option value="failed">Failed</Option>
              </Select>

              <Input
                placeholder="Search Txn Ref ID, Mobile, Biller..."
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchTerm}
                onChange={handleInputChange}
                onPressEnter={(e) => executeSearch(e.target.value)}
                allowClear
                className="bill-payment-search-input"
              />

              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={openMdmModal}
                style={{
                  borderRadius: 8,
                  height: 38,
                  background: "#0284c7",
                  fontWeight: 600,
                }}
              >
                Sync BBPS MDM
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                className="bill-payment-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Card className="bill-payment-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} bill transactions`,
          }}
          scroll={{ x: 1200 }}
          className="enterprise-bill-payment-table"
        />
      </Card>

      {/* Bharat Connect (BBPS) MDM Batch Synchronization Modal */}
      <Modal
        title={
          <Space>
            <FileExcelOutlined style={{ color: "#0284c7", fontSize: 18 }} />
            <span style={{ fontWeight: 700 }}>
              Bharat Connect (BBPS) Master Biller MDM Sync (1,500 / Batch)
            </span>
          </Space>
        }
        open={mdmModalVisible}
        onCancel={() => !batchUploading && setMdmModalVisible(false)}
        footer={null}
        width={680}
      >
        <Alert
          type="info"
          showIcon
          message="BillAvenue MDM Compliance Engine"
          description="Automatically slices large Bharat Connect MDM spreadsheets (22,800+ billers) into safe 1,500-record batches, ingests Fetch_ageing & valadd columns, and syncs encrypted XML biller configurations."
          style={{ marginBottom: 16, borderRadius: 8 }}
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card size="small" style={{ borderRadius: 8, background: "#f8fafc" }}>
              <Statistic
                title="Total MDM Billers"
                value={mdmStats.total_billers}
                valueStyle={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ borderRadius: 8, background: "#f0fdf4" }}>
              <Statistic
                title="XML Configs Synced"
                value={mdmStats.synced_xml_count}
                valueStyle={{ fontWeight: 700, fontSize: 18, color: "#15803d" }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ borderRadius: 8, background: "#fffbeb" }}>
              <Statistic
                title="Pending XML Sync"
                value={mdmStats.pending_xml_count}
                valueStyle={{ fontWeight: 700, fontSize: 18, color: "#b45309" }}
              />
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0" }}>
          Stage 1: Upload BillAvenue MDM Spreadsheet (.xlsx / .csv)
        </Divider>

        <div style={{ marginBottom: 14 }}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            Select Sheet Action Mode:
          </Text>
          <Radio.Group
            value={mdmMode}
            onChange={(e) => setMdmMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="upsert">
              Master MDM (Add / Update Billers)
            </Radio.Button>
            <Radio.Button value="modify">
              Modifications Sheet (Reset &amp; Re-Sync XML)
            </Radio.Button>
            <Radio.Button value="deactivate">
              Deactivation Sheet (Disable Billers)
            </Radio.Button>
          </Radio.Group>
        </div>

        <div
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            padding: 16,
            background: "#f8fafc",
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Space direction="vertical" size={6}>
            <Button
              icon={<FileExcelOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={batchUploading}
            >
              {selectedFileName
                ? `Selected: ${selectedFileName}`
                : "Choose Bharat Connect .xlsx or .csv File"}
            </Button>
            {uploadStatusText && (
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                {uploadStatusText}
              </Text>
            )}
          </Space>

          {uploadProgress > 0 && (
            <div style={{ marginTop: 12 }}>
              <Progress percent={uploadProgress} status="active" />
            </div>
          )}
        </div>

        <Button
          type="primary"
          block
          size="large"
          icon={<CloudUploadOutlined />}
          loading={batchUploading}
          disabled={!parsedRows.length}
          onClick={startBatchIngestion}
          style={{ borderRadius: 8, fontWeight: 600, marginBottom: 16 }}
        >
          Start 1,500-Batch Database Ingestion
        </Button>

        <Divider style={{ margin: "12px 0" }}>
          Stage 2: Fetch BillAvenue XML Biller Info (Max 1,500 / Request)
        </Divider>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f1f5f9",
            padding: "12px 16px",
            borderRadius: 8,
          }}
        >
          <div>
            <Text strong style={{ display: "block" }}>
              BillAvenue XML Parameter Sync (`/v1/billInfo`)
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Fetches input params, partial pay flags &amp; amount rules for up
              to 1,500 pending billers per click.
            </Text>
          </div>
          <Button
            type="default"
            icon={<SyncOutlined spin={xmlSyncing} />}
            loading={xmlSyncing}
            onClick={handleSyncXmlBatch}
            style={{ fontWeight: 600, borderRadius: 8 }}
          >
            Sync Next 1,500 XML
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default BillPaymentList;